import { LOADING_BAR_STATUS_SECONDS } from 'components/LoadingBar/constants';
import { LoadingContextState, LoadingDoneVariant } from 'contexts/LoadingContext';
import { useCallback, useMemo, useState } from 'react';

export interface LoadingStatus {
  status: string;
  message?: string;
}

export const useLoadingContext = (): LoadingContextState => {
  const [loadingItems, setLoadingItems] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<LoadingStatus[]>([]);

  const startLoading = useCallback((item: string) => {
    setLoadingItems((prev) => {
      if (prev.includes(item)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Cant start loading "${item}", it is already in the loadingItems`);
        }
        return prev;
      } else {
        return [...prev, item];
      }
    });
  }, []);

  const shiftStatus = useCallback(() => {
    setStatuses((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const newStatuses = [...prev];
      newStatuses.shift();
      return newStatuses;
    });
  }, []);

  const doneLoading = useCallback(
    (item: string, status: LoadingDoneVariant = 'success', message?: string) => {
      let found = false;

      setLoadingItems((prev) => {
        if (prev.includes(item)) {
          found = true;
          return prev.filter((loadingItem) => loadingItem !== item);
        } else {
          return prev;
        }
      });
      if (found) {
        setStatuses((prev) => [
          ...prev,
          {
            status: `${status}_${item}`,
            message: message,
          },
        ]);
        setTimeout(shiftStatus, 300 + LOADING_BAR_STATUS_SECONDS * 1000);
      }
    },
    [shiftStatus]
  );

  const contextValue = useMemo(() => {
    return { isLoading: !!loadingItems.length, statuses, startLoading, doneLoading, shiftStatus };
  }, [doneLoading, loadingItems, shiftStatus, startLoading, statuses]);

  return contextValue;
};
